// Sentry (errors) + PostHog (product analytics) — go-public observability.
// PII-HARDENED: this app handles SSNs and client financial data, so
//  - Sentry: no default PII, no session replay, request bodies never attached.
//  - PostHog: all session-recording text and inputs are MASKED before leaving
//    the browser; autocapture never captures input values by design.
// The DSN and phc token are public client-side identifiers (safe to ship).
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { scrubUrl } from "./scrub-url";

const SENTRY_DSN = "https://bd2691900c2e2daf53a27af31224b27a@o4511938852487168.ingest.us.sentry.io/4511952428793856";
const POSTHOG_TOKEN = "phc_xdyekNKMFTpLHKWVhfgtA8GRPw3nLqjehe7qPctVG5DY"; // Alief Funding Hub's OWN PostHog project (566455) — never the lead's
const APP_TAG = "alief-funding-hub";

export function initObservability() {
  if (typeof window === "undefined") return;
  if (/^(localhost|127\.|192\.168\.)/.test(window.location.hostname)) return; // dev: stay silent

  // Strip token-bearing URLs out of every Sentry event and transaction. A
  // one-time login/reset token in an error's request.url is a live credential.
  const scrubSentry = (event: any) => {
    try {
      if (event?.request?.url) event.request.url = scrubUrl(event.request.url);
      if (event?.request?.headers?.Referer) event.request.headers.Referer = scrubUrl(event.request.headers.Referer);
      if (event?.request?.query_string) event.request.query_string = undefined;
    } catch { /* never let scrubbing throw */ }
    return event;
  };
  Sentry.init({
    dsn: SENTRY_DSN,
    sendDefaultPii: false,
    tracesSampleRate: 0.2,
    initialScope: { tags: { app: APP_TAG } },
    beforeSend: scrubSentry,
    beforeSendTransaction: scrubSentry,
    // fetch/xhr/navigation breadcrumbs capture the raw request URL — which for
    // token-in-path APIs (intake, details, document-requests, voe) is a live
    // credential. Blank the token before the breadcrumb is attached.
    beforeBreadcrumb: (crumb: any) => {
      try {
        // fetch/xhr use data.url; navigation crumbs use data.from / data.to —
        // scrub every URL-bearing field so no token survives on any of them.
        for (const key of ["url", "from", "to"]) {
          if (typeof crumb?.data?.[key] === "string") crumb.data[key] = scrubUrl(crumb.data[key]);
        }
        if (typeof crumb?.message === "string" && /https?:\/\/|\/(?:api|upload-docs|intake|details|voe|document-requests)\//.test(crumb.message)) {
          crumb.message = scrubUrl(crumb.message);
        }
      } catch { /* never let scrubbing throw */ }
      return crumb;
    },
  });

  posthog.init(POSTHOG_TOKEN, {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: true,
    autocapture: true,
    // Blank the token from every URL property before the event leaves the
    // browser — $current_url, referrers, and any URL autocapture picks up.
    sanitize_properties: (properties: Record<string, any>) => {
      for (const k of ["$current_url", "$pathname", "$referrer", "$referring_domain", "$initial_current_url", "$initial_pathname"]) {
        if (typeof properties[k] === "string") properties[k] = scrubUrl(properties[k]);
      }
      return properties;
    },
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*", // never ship visible SSNs/report data in replays
    },
  });
  posthog.register({ app: APP_TAG });
}
